import { FC, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSignup, ApiError } from '@centrix/api-client';
import type { RegistrantType } from '@centrix/api-client';
import {
  Alert,
  Button,
  CollapsibleContainer,
  FormInputField,
  FormRadioGroupField,
  Heading,
  Icon,
  Paragraph,
} from '@centrix/shared-ui';
import { eyeIcon, eyeOffIcon } from '@centrix/shared-ui/src/icons';

import logoImage from '@/assets/logo.png';
import signupImage from '@/assets/sign-up-back.png';
import {
  normalisePhoneForSubmit,
  type SignupValidationErrors,
  validateSignupFields,
} from '@/utils/authValidation';

import { buildOnboardingPath, markSignupInFlight, resetSignupSession } from './onboarding';

const EMAIL_VERIFICATION_ENABLED =
  import.meta.env.MODE === 'test' || import.meta.env.VITE_ENABLE_EMAIL_VERIFICATION === 'true';
const TERMS_URL = import.meta.env.VITE_TERMS_URL?.trim() || 'https://www.learnovate360.com/terms';

const FORM_INPUT_CLASS_NAME =
  'h-10 px-3 text-xs leading-5 text-black placeholder:text-gray-placeholder focus-visible:ring-offset-0 border-gray-placeholder';
const REGISTRATION_TYPE_OPTIONS = [
  { value: 'company', label: 'Company' },
  { value: 'individual', label: 'Individual Learner' },
] as const;

export const CreateAccountPage: FC = () => {
  const navigate = useNavigate();
  const [registrationType, setRegistrationType] = useState<RegistrantType>('company');
  const [orgName, setOrgName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<SignupValidationErrors>({});

  const signupMutation = useSignup();
  const isLoading = signupMutation.isPending;

  // Reaching the signup page (direct visit, browser Back from a later step,
  // etc.) resets all signup security state — the in-flight flag and every
  // per-email verified flag. This ends any prior in-flight signup AND clears
  // the verified session so pressing browser Forward cannot restore a secure
  // later step (OTP screen, Sector Selection) without re-verifying the OTP.
  useEffect(() => {
    resetSignupSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!EMAIL_VERIFICATION_ENABLED) {
      setError(
        'Email verification is not enabled in this environment. Enable VITE_ENABLE_EMAIL_VERIFICATION before using signup.'
      );
      return;
    }

    const errors = validateSignupFields({
      email,
      password,
      phone,
      orgName,
      registrationType,
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});

    try {
      const submittedPhone = normalisePhoneForSubmit(phone);
      const result = await signupMutation.mutateAsync({
        organisation_name: registrationType === 'company' ? orgName : undefined,
        email,
        password,
        phone_number: submittedPhone || undefined,
        registration_type: registrationType,
      });

      const onboardingToken = result.token;

      markSignupInFlight();
      navigate(
        buildOnboardingPath('/signup/verify-email', {
          email,
          params: { token: onboardingToken },
        }),
        { state: { email, token: onboardingToken } }
      );
    } catch (err) {
      if (err instanceof ApiError) {
        const data = err.data as { error?: string; details?: Record<string, string[]> } | undefined;
        const fields = data?.details;

        if (fields?.email) {
          setError(fields.email[0]);
        } else if (fields?.password) {
          setError(fields.password[0]);
        } else if (fields?.organisation_name) {
          setError(fields.organisation_name[0]);
        } else if (fields?.non_field_errors) {
          setError(fields.non_field_errors[0]);
        } else if (data?.error) {
          setError(data.error);
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row items-stretch bg-white">
      {/* Left Side - Hero Panel (inset card) */}
      <div className="lg:w-3/5 p-12 flex items-center justify-center">
        <div className="w-full h-full bg-brand-panel rounded-2xl flex flex-col items-center justify-center relative overflow-hidden py-12 px-8 min-h-175">
          {/* Heading */}
          <div className="text-center mb-8 z-10">
            <Heading
              as="h1"
              className="text-5xl font-medium text-black leading-14 tracking-[-0.96px]"
            >
              Join Learnovate360!
            </Heading>
            <Paragraph className="text-[28px] text-black leading-10 tracking-[-0.56px] mt-2">
              Join the community of learners
            </Paragraph>
          </div>

          {/* Image with purple ellipse blob */}
          <div className="relative z-10 flex items-end justify-center w-full max-w-145 h-115">
            <img
              src={signupImage}
              alt="Student holding books"
              className="relative z-10 w-full max-w-135 h-auto object-contain"
            />
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="lg:w-2/5 bg-white flex flex-col items-center justify-center py-8 px-8 lg:py-12">
        <div className="w-full max-w-107">
          <div className="flex justify-center mb-8">
            <img src={logoImage} alt="Learnovate360" className="h-18 w-auto" />
          </div>

          <div className="flex flex-col gap-1.5 mb-5">
            <Heading as="h2" className="text-lg font-medium text-black leading-6">
              Create account
            </Heading>
            <Paragraph className="text-xs leading-5 text-gray-body">
              Please provide details for the account creation
            </Paragraph>
          </div>

          <CollapsibleContainer isOpen={!!error} className="mb-4">
            <Alert variant="destructive" onDismiss={() => setError(null)}>
              {error || ''}
            </Alert>
          </CollapsibleContainer>

          <div className="mb-3">
            <FormRadioGroupField
              id="registration_type"
              label="Registering as"
              value={registrationType}
              onValueChange={(value: string) => {
                if (value === registrationType) return;
                setRegistrationType(value as RegistrantType);
                // Shared fields (email/password/phone) intentionally persist
                // across tab switches — they describe the same person either
                // way. orgName is only submitted when registration_type is
                // 'company', so its state cannot leak into an Individual
                // submission. Clear stale validation/server errors so messages
                // from a prior submit attempt do not surface in the new tab.
                setError(null);
                setFieldErrors({});
              }}
              options={[...REGISTRATION_TYPE_OPTIONS]}
              legendClassName="text-xs leading-5 font-medium text-black mb-1.5"
              optionClassName="text-xs leading-6 text-gray-body gap-2"
              radioClassName="h-5 w-5 border-brand-primary text-brand-primary"
            />
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <CollapsibleContainer isOpen={registrationType === 'company'}>
              <FormInputField
                id="orgName"
                label="Organisation Name"
                value={orgName}
                onChange={(e) => {
                  setOrgName(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, orgName: undefined }));
                }}
                placeholder="Enter your company name"
                disabled={isLoading}
                error={fieldErrors.orgName}
                inputClassName={FORM_INPUT_CLASS_NAME}
              />
            </CollapsibleContainer>

            <FormInputField
              id="signupEmail"
              type="email"
              label="Email Address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldErrors((prev) => ({ ...prev, email: undefined }));
              }}
              placeholder="you@company.com"
              disabled={isLoading}
              autoComplete="email"
              error={fieldErrors.email}
              inputClassName={FORM_INPUT_CLASS_NAME}
            />

            <FormInputField
              id="signupPassword"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFieldErrors((prev) => ({ ...prev, password: undefined }));
              }}
              placeholder="Create a strong password"
              disabled={isLoading}
              autoComplete="new-password"
              error={fieldErrors.password}
              inputClassName={`${FORM_INPUT_CLASS_NAME} pr-10`}
              suffix={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-gray-placeholder hover:text-black hover:bg-transparent"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon
                    src={showPassword ? eyeOffIcon : eyeIcon}
                    size="md"
                    className="text-current"
                  />
                </Button>
              }
            />

            <FormInputField
              id="signupPhone"
              type="tel"
              label="Phone Number"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setFieldErrors((prev) => ({ ...prev, phone: undefined }));
              }}
              placeholder="Enter your phone number"
              disabled={isLoading}
              autoComplete="tel"
              error={fieldErrors.phone}
              inputClassName={FORM_INPUT_CLASS_NAME}
            />

            <Button
              type="submit"
              variant="auth"
              size="auth"
              disabled={isLoading}
              className="mt-5 w-full text-sm"
            >
              {isLoading ? 'Creating account...' : 'Signup'}
            </Button>
          </form>

          <Paragraph className="mt-4 text-center text-xs leading-5 text-black">
            By Clicking Signup, you agree to our{' '}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noreferrer"
              className="text-brand-primary font-semibold underline underline-offset-2"
            >
              Terms &amp; Conditions
            </a>
          </Paragraph>

          <Paragraph className="mt-3 text-center text-xs leading-5 text-black">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-primary font-medium hover:underline">
              Login
            </Link>
          </Paragraph>
        </div>
      </div>
    </div>
  );
};
